import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Modal,
  Upload,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../../configs"; // sesuaikan path
import * as XLSX from "xlsx";
import { canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [golongan, setGolongan] = useState(undefined); // placeholder UI
  const [kategori, setKategori] = useState(undefined); // placeholder UI

  const [deletingId, setDeletingId] = useState(null);

  // Bulk upload states
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/item", {
        params: {
          page,
          limit: pageSize,
          item_name: searchName || undefined,
          item_code: searchCode || undefined,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        raw.pagination?.total ||
        payload.total ||
        payload.count ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar komoditas");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for template
  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get("/item-category");
      setCategories(res.data?.data || res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat kategori");
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    setGolongan(undefined);
    setKategori(undefined);
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    // TODO: sambungkan dengan endpoint export Excel kalau sudah ada
    message.info("Fitur export Excel belum dihubungkan dengan API.");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_item);
    try {
      await axiosInstance.delete(`/item/${record.id_item}`);
      message.success("Komoditas berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus komoditas");
    } finally {
      setDeletingId(null);
    }
  };

  // Generate Excel Template
  const handleDownloadTemplate = () => {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Template Data
      const templateData = [
        [
          "Kode Perusahaan*",
          "ID Kategori*",
          "Kode Barang*",
          "HS Code*",
          "Uraian Barang*",
          "Isi Kemasan*",
          "Satuan Kemasan*",
          "Isi Per Unit*",
          "Satuan Unit*",
          "Merk",
          "Kadar Alkohol",
        ],
        [
          "NLO",
          "1",
          "BRG001",
          "22030001",
          "Golongan B (Kadar Etil Alkohol 5% - 20%)",
          "12",
          "CT",
          "750",
          "BO",
          "Brand ABC",
          "15",
        ],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(templateData);

      // Set column widths
      ws1['!cols'] = [
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
        { wch: 35 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
      ];

      XLSX.utils.book_append_sheet(wb, ws1, "Template Komoditas");

      // Sheet 2: Panduan Pengisian
      const guideData = [
        ["PANDUAN PENGISIAN TEMPLATE KOMODITAS"],
        [""],
        ["Field yang wajib diisi ditandai dengan tanda bintang (*)"],
        [""],
        ["1. Kode Perusahaan*"],
        ["   Kode perusahaan pemilik barang"],
        ["   Contoh: NLO, DFI, dll"],
        [""],
        ["2. ID Kategori*"],
        ["   Pilih ID kategori dari tabel referensi di bawah"],
        ["   Contoh: 1, 2, 3, dst"],
        [""],
        ["3. Kode Barang*"],
        ["   Kode unik untuk barang, tidak boleh duplikat"],
        ["   Contoh: BRG001, ITEM-2024-001"],
        [""],
        ["4. HS Code*"],
        ["   Kode HS maksimal 8 digit angka"],
        ["   Contoh: 17019910, 22030001"],
        [""],
        ["5. Uraian Barang*"],
        ["   Deskripsi lengkap barang dan golongan"],
        ["   Contoh: Golongan B (Kadar Etil Alkohol 5% - 20%)"],
        [""],
        ["6. Isi Kemasan*"],
        ["   Jumlah isi dalam kemasan (angka)"],
        ["   Contoh: 12, 24, 6"],
        [""],
        ["7. Satuan Kemasan*"],
        ["   Satuan kemasan, maksimal 10 karakter"],
        ["   Contoh: CT, CG, dll"],
        [""],
        ["8. Isi Per Unit*"],
        ["   Isi per satuan unit (angka)"],
        ["   Contoh: 1000, 500, 250"],
        [""],
        ["9. Satuan Unit*"],
        ["   Satuan per unit, maksimal 10 karakter"],
        ["   Contoh: BO, PCS, PCE, dll"],
        [""],
        ["10. Merk"],
        ["   Merk barang (opsional)"],
        ["   Contoh: Brand ABC, Merk XYZ"],
        [""],
        ["11. Kadar Alkohol"],
        ["   Kadar alkohol dalam persen (opsional)"],
        ["   Contoh: 0, 40, 40.5"],
        ["   Untuk kategori REL/BARANG PROMOSI/INVENTARIS otomatis 0"],
        [""],
        [""],
        ["TABEL REFERENSI ID KATEGORI"],
        ["ID", "Nama Kategori"],
      ];

      // Add categories to guide
      categories.forEach((cat) => {
        guideData.push([
          cat.id_item_category?.toString() || "",
          cat.category || "",
        ]);
      });

      const ws2 = XLSX.utils.aoa_to_sheet(guideData);

      // Set column widths for guide
      ws2['!cols'] = [{ wch: 10 }, { wch: 50 }];

      XLSX.utils.book_append_sheet(wb, ws2, "Panduan");

      // Download file
      XLSX.writeFile(wb, "Template_Komoditas.xlsx");
      message.success("Template berhasil diunduh");
    } catch (err) {
      console.error(err);
      message.error("Gagal mengunduh template");
    }
  };

  // Handle file upload
  const handleFileUpload = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          message.error("File Excel kosong");
          return;
        }

        // Validate and transform data
        const transformedData = jsonData.map((row, index) => {
          const errors = [];

          // Required field validation
          if (!row["Kode Perusahaan*"]) errors.push("Kode Perusahaan wajib diisi");
          if (!row["ID Kategori*"]) errors.push("ID Kategori wajib diisi");
          if (!row["Kode Barang*"]) errors.push("Kode Barang wajib diisi");
          if (!row["HS Code*"]) errors.push("HS Code wajib diisi");
          if (!row["Uraian Barang*"]) errors.push("Uraian Barang wajib diisi");
          if (!row["Isi Kemasan*"]) errors.push("Isi Kemasan wajib diisi");
          if (!row["Satuan Kemasan*"])
            errors.push("Satuan Kemasan wajib diisi");
          if (!row["Isi Per Unit*"]) errors.push("Isi Per Unit wajib diisi");
          if (!row["Satuan Unit*"]) errors.push("Satuan Unit wajib diisi");

          // HS Code validation
          const hsCode = row["HS Code*"]?.toString() || "";
          if (hsCode && !/^[0-9]{1,8}$/.test(hsCode)) {
            errors.push("HS Code harus angka maksimal 8 digit");
          }

          return {
            rowNumber: index + 2, // +2 karena row 1 adalah header
            id_kode_perusahaan: row["Kode Perusahaan*"],
            id_item_category: row["ID Kategori*"],
            item_code: row["Kode Barang*"],
            hs_code: hsCode,
            item_name: row["Uraian Barang*"],
            uraian: row["Uraian Barang*"],
            isi_kemasan: Number(row["Isi Kemasan*"]) || 0,
            satuan_kemasan: row["Satuan Kemasan*"],
            isi_per_unit: Number(row["Isi Per Unit*"]) || 0,
            satuan_per_unit: row["Satuan Unit*"],
            merk: row["Merk"] || null,
            kadar_alkohol: Number(row["Kadar Alkohol"]) || 0,
            errors: errors,
            isValid: errors.length === 0,
          };
        });

        const validData = transformedData.filter((item) => item.isValid);
        const invalidData = transformedData.filter((item) => !item.isValid);

        if (invalidData.length > 0) {
          Modal.warning({
            title: "Data Tidak Valid",
            width: 600,
            content: (
              <div>
                <p>
                  Ditemukan {invalidData.length} baris data yang tidak valid:
                </p>
                <ul style={{ maxHeight: 300, overflow: "auto" }}>
                  {invalidData.slice(0, 10).map((item) => (
                    <li key={item.rowNumber}>
                      Baris {item.rowNumber}: {item.errors.join(", ")}
                    </li>
                  ))}
                  {invalidData.length > 10 && (
                    <li>... dan {invalidData.length - 10} baris lainnya</li>
                  )}
                </ul>
              </div>
            ),
          });
        }

        if (validData.length === 0) {
          message.error("Tidak ada data valid untuk diupload");
          return;
        }

        setUploadedData(validData);
        setUploadModalVisible(false);
        setConfirmModalVisible(true);
      } catch (err) {
        console.error(err);
        message.error("Gagal membaca file Excel");
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent auto upload
  };

  // Handle bulk submit
  const handleBulkSubmit = async () => {
    setUploading(true);
    try {
      const payload = uploadedData.map((item) => ({
        id_kode_perusahaan: item.id_kode_perusahaan,
        id_item_category: item.id_item_category,
        item_code: item.item_code,
        item_name: item.item_name,
        merk: item.merk,
        isi_kemasan: item.isi_kemasan,
        satuan_kemasan: item.satuan_kemasan,
        isi_per_unit: item.isi_per_unit,
        satuan_per_unit: item.satuan_per_unit,
        kadar_alkohol: item.kadar_alkohol,
        hs_code: item.hs_code,
        uraian: item.uraian,
        lampiran_1: null,
        lampiran_2: null,
      }));

      const res = await axiosInstance.post("/item/bulk", { items: payload });

      message.success(
        `Berhasil mengupload ${res.data?.success || uploadedData.length} komoditas`
      );
      setConfirmModalVisible(false);
      setUploadedData([]);
      fetchItems(1, defaultPageSize);
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.error || "Gagal mengupload data komoditas"
      );
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: "No",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Kode Perusahaan",
      dataIndex: "id_kode_perusahaan",
      width: 130,
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "Nama Komoditas",
      dataIndex: "item_name",
      render: (text, record) => text || record.uraian || "-",
    },
    {
      title: "Kode Komoditas",
      dataIndex: "item_code",
      width: 160,
      align: "center",
    },
    {
      title: "Merek",
      dataIndex: "merk",
      width: 160,
      render: (text) => text || "-",
    },
    {
      title: "Tipe / Kategori",
      dataIndex: "category_name",
      width: 180,
      render: (text, record) =>
        text || record.category_name || record.id_item_category || "-",
    },
    {
      title: "Aksi",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Link to={`/referensi/komoditas/edit/${record.id_item}`}>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                style={{
                  background: "#3699FF",
                  borderColor: "#3699FF",
                }}
              />
            </Link>
          </Tooltip>

          {canDelete(MODULES.REFERENSI) && (
            <Popconfirm
              title="Hapus komoditas"
              description={`Yakin ingin menghapus "${record.item_name || record.uraian
                }"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_item,
              }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_item}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Columns for preview modal
  const previewColumns = [
    {
      title: "No",
      dataIndex: "rowNumber",
      width: 60,
      align: "center",
    },
    {
      title: "Kode Perusahaan",
      dataIndex: "id_kode_perusahaan",
      width: 100,
    },
    {
      title: "Kode Barang",
      dataIndex: "item_code",
      width: 120,
    },
    {
      title: "Uraian",
      dataIndex: "item_name",
      ellipsis: true,
    },
    {
      title: "HS Code",
      dataIndex: "hs_code",
      width: 100,
    },
    {
      title: "Kemasan",
      width: 120,
      render: (_, record) =>
        `${record.isi_kemasan} ${record.satuan_kemasan}`,
    },
    {
      title: "Per Unit",
      width: 120,
      render: (_, record) =>
        `${record.isi_per_unit} ${record.satuan_per_unit}`,
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={12} style={{ marginBottom: { xs: 16, md: 0 } }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Komoditas
          </Title>
          <Text type="secondary">
            Kelola daftar komoditas untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Space wrap>
            <Link to="/referensi/komoditas/add">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  background: "#1BC5BD",
                  borderColor: "#1BC5BD",
                  fontWeight: 600,
                }}
              >
                Tambah
              </Button>
            </Link>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
                fontWeight: 600,
              }}
            >
              Template
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
              style={{
                background: "#FFA800",
                borderColor: "#FFA800",
                fontWeight: 600,
              }}
            >
              Upload Bulky
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          border: "1px solid #F3F6F9",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Nama Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari Item..."
              style={{ marginTop: 4 }}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Kode Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari kode..."
              style={{ marginTop: 4 }}
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} style={{ textAlign: "right" }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
                Reset
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  background: "#8950FC",
                  borderColor: "#8950FC",
                }}
              >
                Apply Filter
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        style={{ marginTop: 24 }}
        rowKey="id_item"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `Total ${total} item | Menampilkan ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
        size="small"
      />

      {/* Upload Modal */}
      <Modal
        title="Upload File Excel"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Upload.Dragger
            accept=".xlsx,.xls"
            beforeUpload={handleFileUpload}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 48, color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text">
              Klik atau drag file Excel ke area ini
            </p>
            <p className="ant-upload-hint">
              Pastikan file sesuai dengan template yang telah diunduh
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Upload Data Komoditas"
        open={confirmModalVisible}
        onCancel={() => {
          setConfirmModalVisible(false);
          setUploadedData([]);
        }}
        onOk={handleBulkSubmit}
        okText="Upload Semua"
        cancelText="Batal"
        confirmLoading={uploading}
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            Total data yang akan diupload: {uploadedData.length} komoditas
          </Text>
        </div>
        <Table
          rowKey="rowNumber"
          dataSource={uploadedData}
          columns={previewColumns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
          size="small"
        />
      </Modal>
    </Card>
  );
}
